import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import { api, type EmailTemplate } from '../../lib/api';
import { relativeTime } from '../../lib/dashboardHelpers';
import { C2Button } from '../../components/c2';

export default function AdminEmailsPage() {
  const gate = useRequireSuperAdmin();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gate.ready) return;
    api
      .superEmailTemplates()
      .then(setTemplates)
      .catch(err => setError((err as Error).message || 'Failed to load templates'));
  }, [gate.ready]);

  if (!gate.ready) return null;

  return (
    <AdminShell
      title="Email templates"
      subtitle="Onboarding and trigger-based emails. Edit the copy, change triggers, or add new templates."
      rightSlot={
        <C2Button
          variant="accent"
          size="sm"
          onClick={() => navigate('/admin/emails/new')}
        >
          + New template
        </C2Button>
      }
    >
      {error && (
        <div
          style={{
            padding: 14,
            marginBottom: 16,
            background: 'var(--color-peach)',
            color: 'var(--color-peach-deep)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-surface overflow-hidden"
        style={{
          border: '1px solid var(--color-hairline)',
          borderRadius: 16,
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div
          className="grid bg-bg-alt"
          style={{
            gridTemplateColumns: '160px 1.5fr 2fr 160px 110px 80px 110px',
            gap: 12,
            padding: '10px 20px',
            borderBottom: '1px solid var(--color-hairline)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--color-ink-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <div>Key</div>
          <div>Name</div>
          <div>Subject</div>
          <div>Trigger</div>
          <div>Audience</div>
          <div>Status</div>
          <div>Updated</div>
        </div>

        {!templates ? (
          <div className="text-ink-soft text-sm" style={{ padding: 40, textAlign: 'center' }}>
            Loading…
          </div>
        ) : templates.length === 0 ? (
          <div className="text-ink-muted text-sm" style={{ padding: 40, textAlign: 'center' }}>
            No templates yet. Click <b>New template</b> above to add one.
          </div>
        ) : (
          templates.map(t => (
            <div
              key={t.id}
              onClick={() => navigate(`/admin/emails/${t.id}`)}
              className="grid items-center cursor-pointer transition-colors duration-150"
              style={{
                gridTemplateColumns: '160px 1.5fr 2fr 160px 110px 80px 110px',
                gap: 12,
                padding: '12px 20px',
                borderBottom: '1px solid var(--color-hairline)',
                background: 'var(--color-surface)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--color-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--color-surface)';
              }}
            >
              <span className="font-mono text-sm font-semibold text-ink overflow-hidden text-ellipsis whitespace-nowrap">
                {t.key}
              </span>
              <span className="text-sm text-ink overflow-hidden text-ellipsis whitespace-nowrap">
                {t.name}
              </span>
              <span className="text-sm text-ink-soft overflow-hidden text-ellipsis whitespace-nowrap">
                {t.subject}
              </span>
              <span className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap">
                {t.trigger_summary}
              </span>
              <span>
                <AudienceBadge value={t.audience_filter} />
              </span>
              <span>
                <StatusBadge enabled={t.enabled} />
              </span>
              <span className="text-xs text-ink-muted">{relativeTime(t.updated_at)}</span>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}

function AudienceBadge({ value }: { value: string }) {
  const colors: Record<string, { bg: string; fg: string; label: string }> = {
    all: { bg: 'var(--color-bg-alt)', fg: 'var(--color-ink-soft)', label: 'all' },
    personal_only: { bg: 'var(--color-lilac)', fg: 'var(--color-lilac-deep)', label: 'personal' },
    team_only: { bg: 'var(--color-sky)', fg: 'var(--color-sky-deep)', label: 'team' },
  };
  const c = colors[value] ?? colors.all!;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 6,
        background: c.bg,
        color: c.fg,
      }}
    >
      {c.label}
    </span>
  );
}

function StatusBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 6,
        background: enabled ? 'var(--color-mint)' : 'var(--color-bg-alt)',
        color: enabled ? 'var(--color-mint-deep)' : 'var(--color-ink-muted)',
      }}
    >
      {enabled ? 'enabled' : 'disabled'}
    </span>
  );
}
